/**
 * The pivot surface.
 *
 * Search answers "where does this string appear". This answers "what is in here" —
 * every wallet, every address, every number — which is where an OSINT session actually
 * starts and which no text query can express.
 *
 * Two things on this screen are load-bearing and easy to get wrong.
 *
 * **The quote and the normalized value are separate fields, and the quote is the one in
 * evidence position.** `+7 495 123-45-67` is what the corpus says; `+74951234567` is
 * what we concluded from it, and the conclusion is the part that can be wrong. Rendering
 * only the normalized form would quietly promote an assertion to a fact.
 *
 * **A suppressed entity is hidden, not gone.** The toggle shows it with the reason it
 * was suppressed, because an investigator who can see what the system decided to ignore
 * is the difference between a tool and an oracle.
 */

import { useCallback, useMemo, useState } from "react";
import { api, type ExtractionRun } from "../lib/api";
import { pollCeilingMs, pollToTerminal } from "../lib/poll";
import { useEvidence } from "../state/evidence";
import { useGatedQuery } from "../state/renderGate";

// The five deterministic tier-0 kinds, then the three tier-1 model labels —
// person/location/organisation over source-language text (extraction-tier1-ner).
const CATEGORIES = [
  "crypto",
  "email",
  "phone",
  "url",
  "ip",
  "person",
  "location",
  "organisation",
] as const;

const CATEGORY_LABEL: Record<string, string> = {
  crypto: "Wallets",
  email: "Email",
  phone: "Phones",
  url: "Links",
  ip: "Addresses",
  person: "People",
  location: "Places",
  organisation: "Organisations",
};

const MODEL_CATEGORIES = new Set(["person", "location", "organisation"]);

export function Entities({ datasetId }: { datasetId: string }) {
  const { select, selected } = useEvidence();
  const [category, setCategory] = useState<string>("crypto");
  const [q, setQ] = useState("");
  const [includeSuppressed, setIncludeSuppressed] = useState(false);
  const [sortByScore, setSortByScore] = useState(false);
  const [producer, setProducer] = useState<"tier0" | "tier1">("tier0");
  const [run, setRun] = useState<ExtractionRun | null>(null);
  const [runStale, setRunStale] = useState(false);
  const [runLastChecked, setRunLastChecked] = useState<Date | null>(null);
  const [runGaveUp, setRunGaveUp] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // D1: the query this facet's rows were fetched for, captured at the same moment
  // the request goes out. `useGatedQuery` renders rows only while this still equals
  // the active selection — see `state/renderGate.ts` for why "clear first" cannot
  // reach the same guarantee.
  const query = useMemo(
    () => ({ datasetId, category, q, includeSuppressed, sortByScore }),
    [datasetId, category, q, includeSuppressed, sortByScore],
  );
  const fetchEntities = useCallback(
    (active: typeof query) =>
      api.entities(active.datasetId, {
        category: active.category,
        ...(active.q ? { q: active.q } : {}),
        includeSuppressed: active.includeSuppressed,
        // Triage ordering only (design D5) — there is no score *filter* here or
        // anywhere in this view; the toggle above changes order, never membership.
        ...(active.sortByScore ? { sort: "score" } : {}),
      }),
    [],
  );
  const { rows, error: listError, reload: load } = useGatedQuery(query, fetchEntities);
  const error = runError ?? listError;

  async function extract() {
    setBusy(true);
    setRunError(null);
    setRunGaveUp(false);
    try {
      const started = await api.startExtraction(datasetId, producer);
      // Poll the run rather than the entity list: a run that is still sharding has a
      // partial entity list, and a screen that filled in silently would look like the
      // corpus was thin rather than that the pass was unfinished. Design D4: back off
      // toward a slower cadence and keep polling for up to ten minutes — a real
      // tier-1 pass on real data routinely outlives a 30-second ceiling — and never
      // let a non-terminal count sit on screen without saying it might be stale.
      const outcome = await pollToTerminal(
        () => api.extraction(datasetId, started.run_id),
        (state) => state.status === "ready" || state.status === "failed",
        ({ state, terminal, checkedAt }) => {
          setRun(state);
          setRunStale(!terminal);
          setRunLastChecked(checkedAt);
        },
      );
      setRunGaveUp(outcome.gaveUp);
      await load();
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function checkRunNow() {
    if (!run) return;
    setRunError(null);
    try {
      const state = await api.extraction(datasetId, run.run_id);
      const terminal = state.status === "ready" || state.status === "failed";
      setRun(state);
      setRunStale(!terminal);
      setRunLastChecked(new Date());
      if (terminal) {
        setRunGaveUp(false);
        await load();
      }
    } catch (cause) {
      setRunError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  const declined = run
    ? Object.entries(run.rejections).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="page">
      <h1>Entities</h1>

      <div className="row">
        <select
          aria-label="Extraction producer"
          value={producer}
          onChange={(e) => setProducer(e.target.value as "tier0" | "tier1")}
          data-testid="extraction-producer"
        >
          <option value="tier0">Tier 0 — syntax (wallets, email, IP, URL, phone)</option>
          <option value="tier1">Tier 1 — model (person, location, organisation)</option>
        </select>
        <button
          className="primary"
          onClick={extract}
          disabled={busy}
          data-testid="extract"
        >
          {busy ? "Extracting…" : "Run extraction"}
        </button>
        <input
          aria-label="Filter entities"
          placeholder="Filter by value"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeSuppressed}
            onChange={(e) => setIncludeSuppressed(e.target.checked)}
            data-testid="show-suppressed"
          />
          Show suppressed
        </label>
        {MODEL_CATEGORIES.has(category) && (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={sortByScore}
              onChange={(e) => setSortByScore(e.target.checked)}
              data-testid="sort-by-score"
            />
            Order by model score
          </label>
        )}
      </div>

      {run && (
        <p
          className="mono"
          data-testid="run-summary"
          style={{ color: "var(--ink-faint)" }}
        >
          {run.status === "failed" ? (
            <span style={{ color: "var(--danger)" }}>
              Run failed: {run.error ?? "no error recorded"}
            </span>
          ) : (
            <>
              {run.n_annotations} entities · {run.n_shards_done}/{run.n_shards}{" "}
              shards · {run.producer_version}, guards v{run.guard_version}
              {runStale && runLastChecked && (
                // Design D4: a non-terminal count never presents as final — the
                // count above is whatever the last poll saw, and this is the part
                // that says it may already be behind.
                <span data-testid="run-stale" style={{ fontStyle: "italic" }}>
                  {" "}
                  — still running · last checked{" "}
                  {runLastChecked.toLocaleTimeString()}
                </span>
              )}
            </>
          )}
          {declined.length > 0 && (
            <>
              <br />
              declined:{" "}
              {declined
                .map(([reason, count]) => `${reason} ${count}`)
                .join(" · ")}
              <br />
              <span>
                Declined candidates are counted, not stored. They were never
                entities, and a non-fact in the evidence spine is worse than a
                missing one.
              </span>
            </>
          )}
        </p>
      )}

      {runGaveUp && (
        // Design D4: the ceiling was reached before a terminal state — said plainly,
        // with a way to check again by hand, rather than freezing forever on
        // whatever the last poll happened to see.
        <p data-testid="run-poll-gave-up" style={{ color: "var(--danger)" }}>
          Polling stopped after {Math.round(pollCeilingMs() / 60000) || "<1"} minute(s)
          without the run reaching a final state. It may still be running server-side.{" "}
          <button data-testid="run-refresh" onClick={() => void checkRunNow()}>
            Check now
          </button>
        </p>
      )}

      <nav className="tabs" role="tablist" data-testid="category-tabs">
        {CATEGORIES.map((name) => (
          <button
            key={name}
            role="tab"
            aria-selected={category === name}
            data-testid={`facet-${name}`}
            onClick={() => setCategory(name)}
          >
            {CATEGORY_LABEL[name]}
          </button>
        ))}
      </nav>

      {error && (
        <p data-testid="entities-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      {rows && (
        <p className="eyebrow" data-testid="entity-count">
          {rows.length} {rows.length === 1 ? "occurrence" : "occurrences"}
        </p>
      )}

      <ol className="hits" data-testid="entity-list">
        {rows?.map((row) => (
          <li key={row.annotation_id}>
            <button
              className="hit"
              data-testid="entity"
              aria-current={selected?.blockId === row.block_id}
              onClick={() =>
                select({
                  blockId: row.block_id,
                  spans: [
                    {
                      start_cp: row.start_cp,
                      end_cp: row.end_cp,
                      quote: row.quote,
                    },
                  ],
                  label: row.value_normalized,
                  origin: "entity",
                })
              }
            >
              <span className="eyebrow" data-testid="entity-meta">
                {row.subtype ?? row.category}
                {row.sender_handle ? ` · written by ${row.sender_handle}` : ""}
                {row.score !== null ? ` · score ${row.score.toFixed(2)}` : ""}
                {row.suppressed_reason
                  ? // A suppressed entity an investigator can still see is the point —
                    // the reason and the lexicon version that produced it travel with
                    // the row, never just a bare "hidden" flag.
                    ` · suppressed: ${row.suppressed_reason}` +
                    (row.lexicon_version
                      ? ` (lexicon v${row.lexicon_version})`
                      : "")
                  : ""}
              </span>
              <span className="hit-text mono" data-testid="entity-value">
                {row.value_normalized}
              </span>
              {row.quote !== row.value_normalized && (
                <span className="eyebrow" data-testid="entity-quote">
                  in the source: <span className="mono">{row.quote}</span>
                </span>
              )}
            </button>
          </li>
        ))}
      </ol>

      {rows?.length === 0 && (
        <p data-testid="entities-empty" style={{ color: "var(--ink-faint)" }}>
          Nothing extracted in this category. Run extraction if you have not yet
          — and if you have, this is a finding rather than an error.
        </p>
      )}
    </div>
  );
}
